import { memoizeFunction, IsFocusVisibleClassName } from '@fluentui/react/lib/Utilities';
import {
  IStyle,
  mergeStyleSets,
  FontWeights,
  HighContrastSelector,
  getTheme,
  getFocusStyle,
} from '@fluentui/react/lib/Styling';
import { IFacepileStyles } from '@fluentui/react/lib/Facepile';
import { baseFabricFocusAfterSelector, commonHighContrastOutlineToggledOn } from '@ms/acui-common/lib/styling';
import { appThemedButtonClassNames } from '@ms/acui-button/lib/styling';
import { CoauthorGalleryPalette } from '../Styles';
import { memoizeWithResultCheck } from '@ms/acui-memoization';
import { ICalloutContentStyles, IContextualMenuStyles } from '@fluentui/react';
import { facepileMemberClass } from '../../CollabConstants';
import { shouldUse2PixelFocusBorder } from '@ms/acui-common/lib/experiments';

const topBarHeight = 36; // TODO fix this

export interface CoauthorGalleryStyles {
  root?: IStyle;
  palette?: Partial<CoauthorGalleryPalette>;
  facepileStyles?: IFacepileStyles;
  overflowMenuStyles?: IContextualMenuStyles;
}

export interface CoauthorGalleryClassNames {
  root?: string;
}

export const getCoauthorGalleryClassNames = (styles?: CoauthorGalleryStyles): CoauthorGalleryClassNames => {
  const defaultStyles: CoauthorGalleryStyles = {
    root: {
      display: 'inline-block',
    },
  };
  return mergeStyleSets(defaultStyles, styles);
};

export const getFacepileStyles = memoizeWithResultCheck(
  (palette: CoauthorGalleryPalette, customStyles?: IFacepileStyles): Partial<IFacepileStyles> => {
    return mergeStyleSets(
      {
        root: {
          float: 'left',
          height: topBarHeight,
        },
        itemContainer: {
          height: '100%',
          display: 'flex',
          alignItems: 'center',
        },
        members: {
          height: '100%',
          boxSizing: 'border-box',
          padding: '6px 2px 6px 2px',
          alignItems: 'center',
        },
        member: [
          {
            paddingRight: palette.memberPadding,
            paddingLeft: palette.memberPadding,
            height: palette.memberSize,
            width: palette.memberSize,
            margin: 0,
          },
          facepileMemberClass,
        ],
        itemButton: [
          getFocusStyle(
            getTheme(),
            -2,
            'relative',
            undefined /** highContrastStyle */,
            'transparent',
            palette.focusOutlineColor
          ),
          {
            width: '100%',
            height: '100%',
            borderRadius: 0,
            selectors: {
              [`.${IsFocusVisibleClassName} &:focus`]: {
                backgroundColor: palette.focusBackgroundColor,
              },
              ...(shouldUse2PixelFocusBorder() && {
                [baseFabricFocusAfterSelector]: {
                  outlineWidth: 2,
                  left: 0,
                  right: 0,
                  top: 0,
                  bottom: 0,
                },
              }),
            },
          },
        ],
        descriptiveOverflowButton: {
          width: palette.ribbonCoinSizeFull,
          height: palette.ribbonCoinSizeFull,
          marginRight: palette.overflowButtonMargin,
          marginLeft: palette.overflowButtonMargin,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...(shouldUse2PixelFocusBorder() && {
            selectors: {
              [baseFabricFocusAfterSelector]: {
                outlineWidth: 2,
                left: -3,
                right: -3,
                top: -3,
                bottom: -3,
              },
            },
          }),
        },
        overflowInitialsIcon: [
          appThemedButtonClassNames.label,
          {
            fontWeight: FontWeights.semibold,
            color: palette.overflowInitialsIconColor,
            fontSize: palette.primaryTextSize,
            padding: '2px 0px 2px 0px',
            selectors: {
              [`&.${appThemedButtonClassNames.label}`]: {
                selectors: {
                  [HighContrastSelector]: {
                    color: 'windowtext !important',
                  },
                },
              },
            },
          },
        ],
      },
      customStyles
    );
  }
);

export const getCalloutStyles = memoizeFunction(
  (palette: CoauthorGalleryPalette): Partial<ICalloutContentStyles> => {
    return {
      root: {
        border: `1px solid ${palette.borderColor}`,
        boxShadow: '0 6.4px 14.4px 0 rgba(0, 0, 0, 0.132), 0 1.2px 3.6px 0 rgba(0, 0, 0, 0.108)',
        borderRadius: 2,
        padding: 0,
      },
      beak: {
        transform: 'rotate(45deg)',
        border: `1px solid ${palette.borderColor}`,
        borderBottom: 'none',
        borderRight: 'none',
      },
      beakCurtain: [
        {
          borderRadius: 2,
          borderColor: 'transparent',
          selectors: {
            [HighContrastSelector]: {
              outline: 'none',
            },
          },
        },
        commonHighContrastOutlineToggledOn,
      ],
      calloutMain: { paddingBottom: 4 },
    };
  }
);
